"use client";

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type SyntheticEvent,
} from "react";
import { ChevronDown, ListTree } from "lucide-react";
import { plainText } from "../utils/contentText";

export interface TableOfContentsItem {
  id: string;
  label: string;
}

interface ResponsiveTableOfContentsProps {
  items: TableOfContentsItem[];
  title?: string;
  itemNoun?: string;
}

function keepActiveLinkVisible(nav: HTMLElement | null) {
  if (!nav || nav.offsetParent === null) return;

  const activeLink = nav.querySelector<HTMLElement>('[aria-current="location"]');
  if (!activeLink) return;

  const navRect = nav.getBoundingClientRect();
  const linkRect = activeLink.getBoundingClientRect();

  if (linkRect.top < navRect.top) {
    nav.scrollBy({ top: linkRect.top - navRect.top - 8, behavior: "smooth" });
  } else if (linkRect.bottom > navRect.bottom) {
    nav.scrollBy({
      top: linkRect.bottom - navRect.bottom + 8,
      behavior: "smooth",
    });
  }
}

export default function ResponsiveTableOfContents({
  items,
  title = "Table of Contents",
  itemNoun = "sections",
}: ResponsiveTableOfContentsProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");
  const mobileNavRef = useRef<HTMLElement>(null);
  const desktopNavRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (items.length === 0) return;

    const visibleItems = new Set<string>();
    const itemOrder = new Map(items.map((item, index) => [item.id, index]));

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleItems.add(entry.target.id);
          } else {
            visibleItems.delete(entry.target.id);
          }
        }

        const currentId = [...visibleItems].sort(
          (first, second) =>
            (itemOrder.get(first) ?? 0) - (itemOrder.get(second) ?? 0),
        ).at(-1);

        if (currentId) {
          setActiveId((previousId) =>
            previousId === currentId ? previousId : currentId,
          );
        }
      },
      {
        // Observe a reading band below the fixed navigation. Because each target
        // wraps its complete answer, it remains active while that answer is read.
        rootMargin: "-112px 0px -65% 0px",
        threshold: 0,
      },
    );

    for (const item of items) {
      const section = document.getElementById(item.id);
      if (section) observer.observe(section);
    }

    return () => {
      observer.disconnect();
    };
  }, [items]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      keepActiveLinkVisible(mobileNavRef.current);
      keepActiveLinkVisible(desktopNavRef.current);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeId]);

  const handleItemClick = (
    event: MouseEvent<HTMLAnchorElement>,
    id: string,
  ) => {
    setActiveId(id);
    event.currentTarget.closest("details")?.removeAttribute("open");
  };

  const handleMobileToggle = (event: SyntheticEvent<HTMLDetailsElement>) => {
    if (!event.currentTarget.open) return;

    window.requestAnimationFrame(() => {
      keepActiveLinkVisible(mobileNavRef.current);
    });
  };

  const contentsList = (
    <ol className="list-none space-y-1.5">
      {items.map((item, index) => {
        const isActive = activeId === item.id;

        return (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              onClick={(event) => handleItemClick(event, item.id)}
              className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-colors ${
                isActive
                  ? "bg-[var(--study-primary-soft)] text-[var(--study-primary)] font-semibold"
                  : "study-muted hover:bg-[var(--study-surface-soft)] hover:text-[var(--study-primary)]"
              }`}
              aria-current={isActive ? "location" : undefined}
            >
              <span className="min-w-[2.5ch] flex-shrink-0 whitespace-nowrap text-right font-bold tabular-nums leading-snug">{index + 1}.</span>
              <span className="min-w-0 flex-1 leading-snug">{plainText(item.label)}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
      <details
        className="no-print group study-card rounded-2xl overflow-hidden lg:hidden print:hidden"
        onToggle={handleMobileToggle}
      >
        <summary className="study-title flex cursor-pointer list-none items-center gap-3 p-4 font-bold [&::-webkit-details-marker]:hidden">
          <ListTree className="study-link h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1">{title}</span>
          <span className="study-muted text-xs font-medium">
            {items.length} {itemNoun}
          </span>
          <ChevronDown
            className="study-link h-5 w-5 flex-shrink-0 transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <nav
          ref={mobileNavRef}
          className="max-h-[55vh] overflow-y-auto border-t border-[var(--study-border)] px-3 py-3"
          aria-label={title}
        >
          {contentsList}
        </nav>
      </details>

      <aside className="no-print study-card sticky top-24 hidden rounded-2xl p-4 lg:block print:hidden">
        <h2 className="study-title mb-3 flex items-center gap-2 text-lg font-bold">
          <ListTree className="study-link h-5 w-5" aria-hidden="true" />
          {title}
        </h2>
        <nav
          ref={desktopNavRef}
          className="max-h-[calc(100vh-11rem)] overflow-y-auto pr-1"
          aria-label={title}
        >
          {contentsList}
        </nav>
      </aside>
    </>
  );
}
